import type { ReactNode } from 'react';
import { Mic } from 'lucide-react';
import { cn } from '@/lib/utils';
import prontuarioTrayIcon from '@/assets/icons/daily-tray-prontuario.svg';
import endSessionTrayIcon from '@/assets/icons/daily-tray-end-session.svg';

const PRONTUARIO_TRAY_ACTIVE_RED = '#ef4444';

type P2PTranscriptionCallTrayProps = {
	showStart: boolean;
	showRetry: boolean;
	showEndSession: boolean;
	showProntuario: boolean;
	isProntuarioOpen: boolean;
	isStarting: boolean;
	isEnding: boolean;
	onStart: () => void;
	onRetry: () => void;
	onEndSession: () => void;
	onToggleProntuario: () => void;
};

type TrayButtonVariant = 'default' | 'prontuario' | 'end';

const TRAY_ICON_SLOT_CLASS = 'flex h-8 w-8 items-center justify-center rounded';

const TrayButton = ({
	label,
	onClick,
	disabled,
	active,
	variant = 'default',
	children,
}: {
	label: string;
	onClick: () => void;
	disabled?: boolean;
	active?: boolean;
	variant?: TrayButtonVariant;
	children: ReactNode;
}) => (
	<button
		type="button"
		onClick={onClick}
		disabled={disabled}
		className={cn(
			'flex min-w-[3.75rem] flex-col items-center gap-1 rounded-lg px-2.5 py-1.5 transition-colors',
			'disabled:cursor-not-allowed disabled:opacity-50',
			'hover:bg-white/[0.04] active:bg-white/[0.06]',
		)}
	>
		<span
			className={cn(
				TRAY_ICON_SLOT_CLASS,
				active && variant === 'prontuario' && 'rounded-[4px]',
			)}
			style={
				active && variant === 'prontuario'
					? { backgroundColor: PRONTUARIO_TRAY_ACTIVE_RED }
					: undefined
			}
		>
			{children}
		</span>
		<span
			className={cn(
				'text-[11px] font-medium leading-none',
				variant === 'end' ? 'text-[#ef4444]' : 'text-[#9aa0a6]',
			)}
		>
			{label}
		</span>
	</button>
);

const P2PTranscriptionCallTray = ({
	showStart,
	showRetry,
	showEndSession,
	showProntuario,
	isProntuarioOpen,
	isStarting,
	isEnding,
	onStart,
	onRetry,
	onEndSession,
	onToggleProntuario,
}: P2PTranscriptionCallTrayProps) => {
	if (!showStart && !showRetry && !showEndSession && !showProntuario) {
		return null;
	}

	return (
		<div className="flex shrink-0 items-center justify-center border-t border-white/[0.06] bg-[#0f172a]/95 px-4 py-2.5">
			<div className="flex flex-wrap items-center justify-center gap-1 sm:gap-2">
				{showStart ? (
					<TrayButton
						label={isStarting ? 'Conectando...' : 'Iniciar'}
						onClick={onStart}
						disabled={isStarting || isEnding}
					>
						<Mic
							className="size-[18px] text-white"
							strokeWidth={2}
						/>
					</TrayButton>
				) : null}
				{showRetry ? (
					<TrayButton
						label={
							isStarting ? 'Conectando...' : 'Tentar novamente'
						}
						onClick={onRetry}
						disabled={isStarting || isEnding}
					>
						<Mic
							className="size-[18px] text-white"
							strokeWidth={2}
						/>
					</TrayButton>
				) : null}
				{showProntuario ? (
					<TrayButton
						label="Prontuário"
						onClick={onToggleProntuario}
						active={isProntuarioOpen}
						variant="prontuario"
					>
						<img
							src={prontuarioTrayIcon}
							alt=""
							className="size-[18px]"
						/>
					</TrayButton>
				) : null}
				{showEndSession ? (
					<TrayButton
						label={isEnding ? 'Encerrando...' : 'Encerrar'}
						onClick={onEndSession}
						disabled={isEnding}
						variant="end"
					>
						<img
							src={endSessionTrayIcon}
							alt=""
							className="size-[18px]"
						/>
					</TrayButton>
				) : null}
			</div>
		</div>
	);
};

export { P2PTranscriptionCallTray };
